import { useStore } from 'hooks/useStore';
import {
  DrugReferenceFgNdc,
  DrugReferenceFgNdcPayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceFgNdcInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { debounce } from 'lodash';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  fg_ndc: string;
  drug_name: string;
  bottle_stock_code: number;
  bottle_description: string;
  cap_stock_code: number;
  cap_description: string;
}

export const useDrugReferenceFgNdcTable = (pageSize: number = 100) => {
  const { drugReferenceFgNdcStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<DrugReferenceFgNdc | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    drugReferenceFgNdcStore.fetchDrugReferenceFgNdcs(currentPage, pageSize);
  }, []);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await drugReferenceFgNdcStore.pollDrugReferenceFgNdcs(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
    } catch (error) {
      // Don't break the polling cycle on error
      if(environment === 'development'){
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Drug Reference by NDC data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      drugReferenceFgNdcStore.fetchDrugReferenceFgNdcs(newPage, pageSize, searchQuery || undefined);
    }
  };

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    setSearchQuery(query);
    drugReferenceFgNdcStore.fetchDrugReferenceFgNdcs(1, pageSize, query || undefined);
    setCurrentPage(1); // Reset to first page on new search
  }, 500);

  const handleSearchChange = (query: string) => {
    debouncedSearch(query);
  };

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row
      const updatePayload: DrugReferenceFgNdcPayload = {
        fg_ndc: formData.fg_ndc,
        drug_name: formData.drug_name,
        bottle_stock_code: formData.bottle_stock_code,
        bottle_description: formData.bottle_description,
        cap_stock_code: formData.cap_stock_code,
        cap_description: formData.cap_description
      };

      const success = await drugReferenceFgNdcStore.updateDrugReferenceFgNdc(
        selectedRow.id,
        updatePayload
      );
      if (!success) {
        throw new Error('Failed to update drug reference');
      }
    } else {
      // Add new row
      const createPayload: DrugReferenceFgNdcPayload = {
        fg_ndc: formData.fg_ndc,
        drug_name: formData.drug_name,
        bottle_stock_code: formData.bottle_stock_code,
        bottle_description: formData.bottle_description,
        cap_stock_code: formData.cap_stock_code,
        cap_description: formData.cap_description,
        flag: true
      };

      const success = await drugReferenceFgNdcStore.createDrugReferenceFgNdc(createPayload);
      if (!success) {
        throw new Error('Failed to create drug reference');
      }
    }
    setSelectedRow(null);
    // Refresh data while maintaining current page
    await drugReferenceFgNdcStore.fetchDrugReferenceFgNdcs(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleDeleteRow = async () => {
    if (selectedRow) {
      await drugReferenceFgNdcStore.deleteDrugReferenceFgNdc(selectedRow.id);

      // After delete, check if we need to adjust the current page
      const totalPages = drugReferenceFgNdcStore.pagination.totalPages;
      const newPage = currentPage > totalPages ? Math.max(totalPages, 1) : currentPage;

      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }

      // Refetch data for the current page
      drugReferenceFgNdcStore.fetchDrugReferenceFgNdcs(newPage, pageSize, searchQuery || undefined);

      setSelectedRow(null);
      setShowDeleteModal(false);
    }
  };

  const prepareFormData = (row: DrugReferenceFgNdc): FormData => {
    return {
      fg_ndc: row.fg_ndc || '',
      drug_name: row.drug_name || '',
      bottle_stock_code: row.bottle_stock_code || 0,
      bottle_description: row.bottle_description || '',
      cap_stock_code: row.cap_stock_code || 0,
      cap_description: row.cap_description || ''
    };
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await drugReferenceFgNdcStore.refreshData(currentPage, pageSize, searchQuery || undefined);
  };

  const handleExportToExcel = () => {
    const filteredData = drugReferenceFgNdcStore.drugReferenceFgNdcs.filter(item => {
      if (!searchQuery.trim()) {
        return true;
      }
      const searchLower = searchQuery.toLowerCase();
      return (
        item.fg_ndc?.toLowerCase().includes(searchLower) ||
        item.drug_name?.toLowerCase().includes(searchLower) ||
        item.bottle_description?.toLowerCase().includes(searchLower) ||
        item.cap_description?.toLowerCase().includes(searchLower) ||
        item.bottle_stock_code?.toString().includes(searchQuery) ||
        item.cap_stock_code?.toString().includes(searchQuery)
      );
    });

    const exportData = filteredData.map(item => ({
      'NDC': item.fg_ndc,
      'Drug Name': item.drug_name,
      'Bottle Stock Code': item.bottle_stock_code,
      'Bottle Description': item.bottle_description,
      'Cap Stock Code': item.cap_stock_code,
      'Cap Description': item.cap_description
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Drug Reference by NDC');
    XLSX.writeFile(workbook, 'drug_reference_by_ndc_export.xlsx');
  };

  return {
    drugReferenceData: drugReferenceFgNdcStore.drugReferenceFgNdcs,
    isLoading: drugReferenceFgNdcStore.isLoading,
    pagination: drugReferenceFgNdcStore.pagination,
    searchQuery,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    handleSearchChange,
    prepareFormData,
    onPageChange,
    currentPage,
    refreshData,
    handleExportToExcel
  };
};
